import { useEffect, useState } from "react";
import Button from "../organism/Button";
import CustomText from "../atomic/CustomText";
import useAdBlockDetector from "../global/DetectAdBlock";
import toastMessages from "../../services/utils/helpers/toastMessage";

function ReportsPopup({ reports }: { reports: string[] }) {
  const [currentReport, setCurrentReport] = useState(0);
  const AdBlockerEnabled = useAdBlockDetector();
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [showFallbackWarning, setShowFallbackWarning] = useState(false);

  useEffect(() => {
    const updateMobileState = () => {
      setIsMobile(window.innerWidth < 768);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateMobileState);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", updateMobileState);
      }
    };
  }, []);

  useEffect(() => {
    if (AdBlockerEnabled) {
      toastMessages.warning(
        "Ad Blocker Detected. Please disable your ad blocker to use all features of the application"
      );
    }
    setShowFallbackWarning(AdBlockerEnabled || isMobile);
  }, [AdBlockerEnabled, isMobile, reports]);

  const handleReportNavigation = (index: number) => {
    setCurrentReport(index);
    if (AdBlockerEnabled || isMobile) {
      window.open(reports[index], "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-w-[35vw] lg:min-w-[30vw]">
      {showFallbackWarning && (
        <div className="p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow-sm">
          <div className="flex items-start">
            <div className="ml-3">
              <h3 className="text-lg font-medium text-yellow-800">
                PDF Viewer Blocked
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  {AdBlockerEnabled
                    ? "We've detected an ad blocker that may be preventing the report from displaying properly. Please open the report in a new tab."
                    : "PDF preview is not supported on mobile. Please open the report in a new tab."}
                </p>
                <div className="mt-4">
                  <a
                    href={reports[currentReport]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary hover:shadow-primaryHover hover:bg-primaryHover shadow-sm shadow-primary rounded-md uppercase text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    Open PDF in New Tab
                  </a>
                </div>
                <p className="mt-4 text-yellow-600">
                  Still having trouble?{" "}
                  <a
                    href="mailto:clinicalservices@castlebiosciences.com"
                    className="font-medium underline hover:text-yellow-500"
                  >
                    Contact support
                  </a>{" "}
                  {AdBlockerEnabled
                    ? "or try disabling your ad blocker."
                    : "or try opening the report from a desktop browser."}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!AdBlockerEnabled && !isMobile && (
        <embed
          width={250}
          height={250}
          src={reports[currentReport]}
          className="h-[80vh] w-full object-contain"
        />
      )}

      {reports.length > 1 && (
        <div className="flex items-center mt-4 justify-between">
          <div>
            <CustomText
              text={`${currentReport + 1} of ${reports.length}`}
              fontWeight="font-normal"
              fontSize="text-sm"
            />
          </div>
          <div className="flex items-center">
            <Button
              textInformation={{ text: "Previous", fontWeight: "font-normal" }}
              disabled={currentReport === 0}
              loading={false}
              className="mx-3"
              buttonTheme="primary"
              onClick={() => handleReportNavigation(currentReport - 1)}
            />
            <Button
              textInformation={{ text: "Next", fontWeight: "font-normal" }}
              disabled={currentReport === reports.length - 1}
              loading={false}
              buttonTheme="primary"
              onClick={() => handleReportNavigation(currentReport + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsPopup;
